import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getHoldingsListForPerson } from '@dealstack/frontend/app/services/Holdings.service.ts';
import { useSearchParams } from 'react-router-dom';
import { formatAsOfFilterDate } from '@dealstack/frontend/shared/utils/utc.ts';
import { CapTableRail } from '@dealstack/shared/lib/generated';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';

export enum ViewMode {
	Wide = 'wide',
	Regular = 'regular',
	Block = 'block',
}

export enum HoldingsType {
	HoldingsTable = 'holdings',
	OwnershipTable = 'ownership',
}

interface Props {
	tableType: HoldingsType;
}

enum PARAM_KEYS {
	VIEW_MODE = 'viewMode',
	FILTER_DATE = 'filterDate',
}

export const useHoldingsController = ({ tableType }: Props) => {
	const { selectedEntityId, selectedEntityName, dealId, selectedEntityData } = useContext(
		CapManageControllerContext
	);
	const { deal } = useContext(DealContext) || {};

	const [searchParams, setSearchParams] = useSearchParams();
	const prevEntityId = useRef(selectedEntityId);

	const defaultViewMode =
		tableType === HoldingsType.HoldingsTable ? ViewMode.Regular : ViewMode.Wide;

	const [showSummarized, setShowSummarized] = useState<boolean>(false);
	const [showPercentages, setShowPercentages] = useState<boolean>(false);
	const urlFilterDate = searchParams.get(PARAM_KEYS.FILTER_DATE);
	const initialFilterDate = urlFilterDate ? new Date(urlFilterDate) : null;
	const [filterDate, setFilterDateInternal] = useState<Date | null>(initialFilterDate);
	const urlViewMode = searchParams.get(PARAM_KEYS.VIEW_MODE) as ViewMode;
	const initialViewMode =
		urlViewMode && Object.values(ViewMode).includes(urlViewMode)
			? urlViewMode
			: defaultViewMode;

	const [viewMode, setViewModeInternal] = useState<ViewMode>(initialViewMode);

	// CAP-1973: nominee-structure title rail. Beneficial is the default; the toggle is only
	// surfaced (and the rail only sent) when railEnabled below is true.
	const [rail, setRail] = useState<CapTableRail>(CapTableRail.BENEFICIAL);

	const setViewMode = (newViewMode: ViewMode) => {
		setViewModeInternal(newViewMode);

		const newParams = new URLSearchParams(searchParams);
		newParams.set(PARAM_KEYS.VIEW_MODE, newViewMode);
		setSearchParams(newParams, { replace: true });
	};

	const setFilterDate = (newDate: Date | null) => {
		setFilterDateInternal(newDate);

		const newParams = new URLSearchParams(searchParams);
		if (newDate) {
			newParams.set(PARAM_KEYS.FILTER_DATE, newDate.toISOString());
		} else {
			newParams.delete(PARAM_KEYS.FILTER_DATE);
		}
		setSearchParams(newParams, { replace: true });
	};

	useEffect(() => {
		if (prevEntityId.current && prevEntityId.current !== selectedEntityId) {
			setFilterDateInternal(null);

			const newParams = new URLSearchParams(searchParams);
			newParams.delete(PARAM_KEYS.FILTER_DATE);

			const currentViewMode = newParams.get(PARAM_KEYS.VIEW_MODE) as ViewMode;
			if (!currentViewMode || !Object.values(ViewMode).includes(currentViewMode)) {
				newParams.set(PARAM_KEYS.VIEW_MODE, viewMode);
			}

			setSearchParams(newParams, { replace: true });
		}
		prevEntityId.current = selectedEntityId;
	}, [selectedEntityId, searchParams, setSearchParams, viewMode]);

	useEffect(() => {
		if (!urlViewMode) {
			const newParams = new URLSearchParams(searchParams);
			newParams.set(PARAM_KEYS.VIEW_MODE, defaultViewMode);
			setSearchParams(newParams, { replace: true });
		}
	}, []);

	const isOwnership = tableType !== HoldingsType.HoldingsTable;
	const viewByBlock = viewMode === ViewMode.Block;

	// CAP-1976: the feature flag keeps the whole nominee surface dark, and Deal.hasNomineeStructure
	// is the master gate. On Ownership the selected entity is the ISSUER, so its own flag counts
	// too; on Holdings the entity is the holder and the table spans every issuer it holds in, so
	// the deal-level switch is the only meaningful gate. The gate lives here so the table request,
	// the rail toggle and the exports can never disagree: when it is off, no rail is sent at all
	// and the backend reads the single register, exactly as it does for a switched-off deal.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;
	const railEnabled =
		nomineeStructuresEnabled &&
		!!deal?.hasNomineeStructure &&
		(!isOwnership || !!selectedEntityData?.hasNomineeStructure);
	const effectiveRail = railEnabled ? rail : undefined;

	const getDataListRequest = useQuery({
		queryKey: [
			API_PATHS.CAP_HOLDINGS_LIST_FOR_PERSON_POST,
			{
				personId: selectedEntityId,
				dealId,
				filterDate: formatAsOfFilterDate(filterDate),
				isOwnership,
				viewByBlock,
				rail: effectiveRail,
			},
		],
		queryFn: () =>
			getHoldingsListForPerson(API_PATHS.CAP_HOLDINGS_LIST_FOR_PERSON_POST, {
				personId: selectedEntityId,
				filterDate: formatAsOfFilterDate(filterDate),
				isOwnership,
				viewByBlock,
				rail: effectiveRail,
			}),
		// CAP-1973: switching the beneficial/legal rail changes the query key. keepPreviousData
		// was previously passed inside the queryKey array (a no-op in react-query v5), so the
		// table flashed empty on every toggle. As a real query option it keeps the previous
		// rail's rows on screen until the new ones arrive.
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		void refetchHoldingsList();
	}, [viewByBlock]);

	const refetchHoldingsList = getDataListRequest.refetch;

	const onSummarizedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setShowSummarized(event.target.checked);
	};

	const onShowPercentagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setShowPercentages(event.target.checked);
	};

	return {
		showSummarized,
		showPercentages,
		selectedEntityName,
		selectedEntityId,
		filterDate,
		setFilterDate,
		getHoldingsListRequest: getDataListRequest,
		getOwnershipListRequest: getDataListRequest,
		viewMode,
		setViewMode,
		onSummarizedChange,
		onShowPercentagesChange,
		isEmptyState: !getDataListRequest.data?.length,
		refetchHoldingsList,
		selectedEntityData,
		viewByBlock,
		rail,
		setRail,
		railEnabled,
		effectiveRail,
	};
};
